import {
  Card as NUICard,
  CardBody as NUICardBody,
  CardFooter as NUICardFooter,
  CardHeader as NUICardHeader,
} from "@nextui-org/card";
import clsx from "clsx";
import { motion } from "framer-motion";

import { CardBodyProps, CardFooterProps, CardHeaderProps, CardProps } from "./Card.types";

export const Card: React.FC<CardProps> & {
  Body: React.FC<CardBodyProps>;
  Header: React.FC<CardHeaderProps>;
  Footer: React.FC<CardFooterProps>;
} = ({ children, className, id, omitDecoration }) => {
  return (
    // `id` is forwarded to the NextUI card so callers can target the card element
    // itself; the motion wrapper gets a derived `-motion` id for animation debugging.
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      id={id ? `${id}-motion` : undefined}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.2 }}
    >
      <NUICard
        className={clsx("relative overflow-visible rounded bg-zinc-900 p-4", className)}
        id={id}
      >
        {!omitDecoration && (
          <div
            className="absolute right-[-35px] top-[-35px] hidden h-[70px] w-[70px] rotate-45 border-b-1 border-b-gray-800 bg-background sm:block"
            id={id ? `${id}-corner-decoration` : undefined}
          />
        )}
        {children}
      </NUICard>
    </motion.div>
  );
};

const CardBody: React.FC<CardBodyProps> = ({ children, className, ...props }) => (
  <NUICardBody className={clsx("overflow-y-hidden px-0 pb-0", className)} {...props}>
    {children}
  </NUICardBody>
);

const CardHeader: React.FC<CardHeaderProps> = ({ children, className, ...props }) => (
  <NUICardHeader className={clsx("z-[1] p-0", className)} {...props}>
    {children}
  </NUICardHeader>
);

const CardFooter: React.FC<CardFooterProps> = ({ children, className, ...props }) => (
  <NUICardFooter className={clsx(className)} {...props}>
    {children}
  </NUICardFooter>
);

Card.Body = CardBody;
Card.Header = CardHeader;
Card.Footer = CardFooter;
